import {
  assertPublicationForAdapter,
  clonePublicationForAdapter,
} from "../StudioPublicationAdapter";

export class PublicationRuntimeBridgeError extends Error {
  constructor(
    message,
    {
      code = "PUBLICATION_RUNTIME_BRIDGE_ERROR",
      operation = null,
      stage = null,
      domainId = null,
      publicationId = null,
      details = {},
      cause = null,
    } = {}
  ) {
    super(message);

    this.name = "PublicationRuntimeBridgeError";
    this.code = code;
    this.operation = operation;
    this.stage = stage;
    this.domainId = domainId;
    this.publicationId = publicationId;

    this.details = Object.freeze({
      ...details,
    });

    if (cause !== null) {
      this.cause = cause;
    }
  }
}

/**
 * Generic bridge between a resolved Studio publication and a
 * domain Runtime configuration.
 *
 * The bridge owns orchestration only.
 *
 * It does not:
 * - resolve repositories;
 * - resolve LayoutVersion directly;
 * - publish Studio documents;
 * - mutate descriptors;
 * - contain domain logic;
 * - maintain global state;
 * - cache Runtime configurations.
 */
export function createPublicationRuntimeBridge({
  activePublicationResolver,
  publicationAdapterRegistry,
} = {}) {
  assertActivePublicationResolver(
    activePublicationResolver
  );

  assertPublicationAdapterRegistry(
    publicationAdapterRegistry
  );

  async function resolveActiveRuntimeConfiguration(
    scope
  ) {
    const operation =
      "resolveActiveRuntimeConfiguration";

    let descriptor;

    try {
      descriptor =
        await activePublicationResolver
          .resolveActivePublication(scope);
    } catch (error) {
      throw wrapStageError(
        error,
        {
          code:
            "ACTIVE_PUBLICATION_RESOLUTION_FAILED",
          operation,
          stage: "resolvePublication",
        }
      );
    }

    if (descriptor === null) {
      return null;
    }

    return resolveDescriptorRuntimeConfiguration(
      descriptor,
      {
        operation,
      }
    );
  }

  async function resolveRuntimeConfigurationByPublicationId(
    publicationId,
    scope
  ) {
    const operation =
      "resolveRuntimeConfigurationByPublicationId";

    let descriptor;

    try {
      descriptor =
        await activePublicationResolver
          .resolvePublicationById(
            publicationId,
            scope
          );
    } catch (error) {
      throw wrapStageError(
        error,
        {
          code:
            "PUBLICATION_RESOLUTION_FAILED",
          operation,
          stage: "resolvePublication",
          publicationId:
            normalizeOptionalString(
              publicationId
            ),
        }
      );
    }

    return resolveDescriptorRuntimeConfiguration(
      descriptor,
      {
        operation,
      }
    );
  }

  async function resolveDescriptorRuntimeConfiguration(
    descriptor,
    {
      operation =
        "resolveDescriptorRuntimeConfiguration",
    } = {}
  ) {
    const domainId =
      normalizeDescriptorIdentity(
        descriptor,
        "domainId"
      );

    const publicationId =
      normalizeDescriptorIdentity(
        descriptor,
        "publicationId"
      );

    let adapter;

    try {
      adapter =
        publicationAdapterRegistry
          .getPublicationAdapter(
            domainId
          );
    } catch (error) {
      throw wrapStageError(
        error,
        {
          code:
            "PUBLICATION_ADAPTER_RESOLUTION_FAILED",
          operation,
          stage: "resolveAdapter",
          domainId,
          publicationId,
        }
      );
    }

    let migratedDescriptor;

    try {
      const publicationCopy =
        clonePublicationForAdapter(
          descriptor
        );

      migratedDescriptor =
        await adapter.migratePublication(
          publicationCopy
        );
    } catch (error) {
      throw wrapStageError(
        error,
        {
          code:
            "PUBLICATION_MIGRATION_FAILED",
          operation,
          stage: "migratePublication",
          domainId,
          publicationId,
        }
      );
    }

    try {
      assertPublicationForAdapter(
        adapter,
        migratedDescriptor
      );
    } catch (error) {
      throw wrapStageError(
        error,
        {
          code:
            "PUBLICATION_COMPATIBILITY_FAILED",
          operation,
          stage: "assertPublicationForAdapter",
          domainId,
          publicationId,
        }
      );
    }

    let validation;

    try {
      validation =
        await adapter.validatePublication(
          migratedDescriptor
        );
    } catch (error) {
      throw wrapStageError(
        error,
        {
          code:
            "PUBLICATION_VALIDATION_FAILED",
          operation,
          stage: "validatePublication",
          domainId,
          publicationId,
        }
      );
    }

    assertPublicationValidationResult(
      validation,
      {
        operation,
        domainId,
        publicationId,
      }
    );

    let deserializedPublication;

    try {
      deserializedPublication =
        await adapter.deserializePublication(
          migratedDescriptor
        );
    } catch (error) {
      throw wrapStageError(
        error,
        {
          code:
            "PUBLICATION_DESERIALIZATION_FAILED",
          operation,
          stage: "deserializePublication",
          domainId,
          publicationId,
        }
      );
    }

    try {
      return await adapter
        .resolveRuntimeConfiguration(
          deserializedPublication,
          migratedDescriptor
        );
    } catch (error) {
      throw wrapStageError(
        error,
        {
          code:
            "RUNTIME_CONFIGURATION_RESOLUTION_FAILED",
          operation,
          stage:
            "resolveRuntimeConfiguration",
          domainId,
          publicationId,
        }
      );
    }
  }

  return Object.freeze({
    resolveActiveRuntimeConfiguration,
    resolveRuntimeConfigurationByPublicationId,
  });
}

function assertActivePublicationResolver(
  resolver
) {
  if (
    resolver === null ||
    typeof resolver !== "object"
  ) {
    throw new PublicationRuntimeBridgeError(
      "activePublicationResolver must be an object.",
      {
        code:
          "INVALID_ACTIVE_PUBLICATION_RESOLVER",
      }
    );
  }

  for (const methodName of [
    "resolveActivePublication",
    "resolvePublicationById",
  ]) {
    if (
      typeof resolver[methodName] !==
      "function"
    ) {
      throw new PublicationRuntimeBridgeError(
        `activePublicationResolver requires ${methodName}().`,
        {
          code:
            "INVALID_ACTIVE_PUBLICATION_RESOLVER",
          details: {
            methodName,
          },
        }
      );
    }
  }
}

function assertPublicationAdapterRegistry(
  registry
) {
  if (
    registry === null ||
    typeof registry !== "object" ||
    typeof registry.getPublicationAdapter !==
      "function"
  ) {
    throw new PublicationRuntimeBridgeError(
      "publicationAdapterRegistry requires getPublicationAdapter().",
      {
        code:
          "INVALID_PUBLICATION_ADAPTER_REGISTRY",
      }
    );
  }
}

function assertPublicationValidationResult(
  validation,
  {
    operation,
    domainId,
    publicationId,
  }
) {
  if (
    validation === null ||
    typeof validation !== "object" ||
    Array.isArray(validation) ||
    typeof validation.valid !== "boolean"
  ) {
    throw new PublicationRuntimeBridgeError(
      "validatePublication() must return an object containing a boolean valid property.",
      {
        code:
          "INVALID_PUBLICATION_VALIDATION_RESULT",
        operation,
        stage: "validatePublication",
        domainId,
        publicationId,
      }
    );
  }

  if (!validation.valid) {
    throw new PublicationRuntimeBridgeError(
      "Studio publication failed domain validation.",
      {
        code:
          "PUBLICATION_VALIDATION_FAILED",
        operation,
        stage: "validatePublication",
        domainId,
        publicationId,
        details: {
          issues: Array.isArray(
            validation.issues
          )
            ? validation.issues
            : [],
        },
      }
    );
  }

  return validation;
}

function normalizeDescriptorIdentity(
  descriptor,
  fieldName
) {
  if (
    descriptor === null ||
    typeof descriptor !== "object" ||
    typeof descriptor[fieldName] !==
      "string" ||
    descriptor[fieldName].trim() === ""
  ) {
    throw new PublicationRuntimeBridgeError(
      `Resolved descriptor requires ${fieldName}.`,
      {
        code:
          "INVALID_RESOLVED_PUBLICATION",
        details: {
          fieldName,
        },
      }
    );
  }

  return descriptor[fieldName].trim();
}

function normalizeOptionalString(value) {
  return (
    typeof value === "string" &&
    value.trim() !== ""
  )
    ? value.trim()
    : null;
}

function wrapStageError(
  error,
  {
    code,
    operation,
    stage,
    domainId = null,
    publicationId = null,
  }
) {
  if (
    error instanceof
    PublicationRuntimeBridgeError
  ) {
    return error;
  }

  return new PublicationRuntimeBridgeError(
    `Publication Runtime Bridge failed during ${stage}.`,
    {
      code,
      operation,
      stage,
      domainId,
      publicationId,
      cause: error,
    }
  );
}
